import {useState} from 'react';
import {useMutation} from 'react-query';
import {useLocation} from 'react-router-dom';
import CheckoutWrapper from 'components/Checkouts/Checkout/common/CheckoutWrapper';
import CustomInputBox from 'utils/CustomInput/CustomInputBox';
import CustomPhoneInput from 'utils/CustomInput/CustomPhoneInput';
import {initializeCheckout} from 'api/routes/wallet';
import {getError, getPhone, getVersion, toCurrency} from 'utils/helpers';
import {formValidator} from 'utils/FormValidator/FormValidator';
import {useDebounce, useSupportedCurrencies} from 'hooks';
import {useLASGCharges} from './useLASGQuery';
import LASGHeader from './LASGHeader';
import LASGInvoiceCard from './LASGInvoiceCard';
import LASGChargesBreakdown from './LASGChargesBreakdown';
import CustomSpinner from 'utils/Spinner/Spinner';
import Alert from 'utils/Alert/Alert';
import {currencies} from 'components/constants';

const LASGPaymentView = ({invoiceData}) => {
    const location = useLocation();
    const maxAmount = Number(invoiceData?.maxAmount) || Number(invoiceData?.amountDue) || 0;
    const canEdit = invoiceData?.paymentFlag === 'PartPayment';

    const {minimumAmountByCurrency} = useSupportedCurrencies(invoiceData?.publicKey);
    const minAmount = minimumAmountByCurrency?.[currencies.NGN] || 50;

    const [amount, setAmount] = useState(canEdit ? maxAmount : Number(invoiceData?.amountDue) || 0);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [errorMsg, setErrorMsg] = useState('');

    const {API, apiVersion} = getVersion(location);

    const debouncedAmount = useDebounce(amount, 600);
    const {
        chargesData = {},
        isLoading: chargesLoading,
        isFetching,
    } = useLASGCharges({amount: debouncedAmount, publicKey: invoiceData.publicKey});

    const initMutation = useMutation(
        async payAmount => {
            const returnUrl = `${window.location.origin}/payments/lasg/receipt`;
            const response = await API.post(
                `${apiVersion}${initializeCheckout}`,
                {
                    walletId: 'master',
                    amount: payAmount,
                    currency: currencies.NGN,
                    metadata: {
                        lasg_reference: invoiceData?.reference,
                        product_reference: invoiceData?.reference,
                        email: email.trim(),
                        phone,
                    },
                    returnUrl,
                },
                {headers: {Authorization: invoiceData.publicKey}}
            );
            if (!response.data?.status && !response.data?.success) {
                setErrorMsg(response.data?.message || 'Failed to initialize payment.');
            }
            return response.data;
        },
        {
            onSuccess: data => {
                window.location.href = `/payments/${data?.data?.reference}`;
            },
            onError: error => {
                setErrorMsg(getError(error));
            },
        }
    );

    const validateAmount = value => {
        const num = Number(value);
        if (!num || isNaN(num)) return 'Please enter a valid amount.';

        if (num < minAmount) {
            return `Minimum payable amount is ₦${toCurrency(minAmount)}.`;
        }
        if (num > maxAmount) return `Maximum payable amount is ₦${toCurrency(maxAmount)}.`;
        return '';
    };

    const handlePay = () => {
        const {formErrors, isValid} = formValidator({email, phone});
        const amountError = canEdit ? validateAmount(amount) : '';

        if (!isValid || amountError) {
            setFieldErrors({...formErrors, ...(amountError && {amount: amountError})});
            return;
        }

        initMutation.mutate(Number(amount));
    };

    return (
        <CheckoutWrapper isFrame={false} sendEvent={() => {}}>
            <LASGHeader />
            <div className="p-5">
                {errorMsg && <Alert msg={errorMsg} cls="px-5 mt-4" onClose={() => setErrorMsg()} />}
                <LASGInvoiceCard invoiceData={invoiceData} />
                <LASGChargesBreakdown
                    chargesData={{...chargesData, amount}}
                    isLoading={chargesLoading || isFetching}
                />
                <div className="flex__between align-items-start gap-3">
                    <CustomInputBox
                        name="email"
                        label="Email Address"
                        placeholder="Enter email address"
                        type="email"
                        value={email}
                        onChange={({target}) => {
                            setEmail(target.value);
                            setFieldErrors(prev => ({...prev, email: ''}));
                        }}
                        error={fieldErrors.email}
                        cls="w-50"
                        required
                    />
                    <CustomPhoneInput
                        name="phone"
                        label="Phone Number"
                        placeholder="Enter phone number"
                        value={phone}
                        onChange={value => {
                            setPhone(getPhone(value));
                            setFieldErrors(prev => ({...prev, phone: ''}));
                        }}
                        error={fieldErrors.phone}
                        cls="w-50"
                        required
                    />
                </div>
                {canEdit && (
                    <div className="mb-3">
                        <CustomInputBox
                            name="amount"
                            label="Payment Amount (NGN)"
                            placeholder="Enter amount"
                            initialValue={amount}
                            value={amount}
                            onChange={({target}) => {
                                setAmount(target.value);
                                setFieldErrors(prev => ({
                                    ...prev,
                                    amount: validateAmount(target.value),
                                }));
                            }}
                            error={fieldErrors.amount}
                            cls="mb-1"
                        />
                    </div>
                )}

                <button
                    className="btn btn--sm btn--primary w-75 mx-auto mt-4"
                    onClick={handlePay}
                    disabled={initMutation.isLoading}
                >
                    {initMutation.isLoading ? <CustomSpinner /> : 'Pay Now'}
                </button>
            </div>
        </CheckoutWrapper>
    );
};

export default LASGPaymentView;
